<template>
  <div class="admin-upload-container">
    <div class="header">
      <van-nav-bar title="批量上传题目" left-arrow @click-left="goBack" />
    </div>

    <div class="content">
      <div class="format-section">
        <h3>📋 题目格式说明</h3>
        <div class="format-card">
          <p class="format-title">支持格式：Excel (.xlsx) 或 CSV (.csv)</p>
          <div class="format-example">
            <p class="example-title">单选题/多选题格式：</p>
            <table class="format-table">
              <thead>
                <tr>
                  <th>题目标题</th>
                  <th>题目类型</th>
                  <th>选项A</th>
                  <th>选项B</th>
                  <th>选项C</th>
                  <th>选项D</th>
                  <th>正确答案</th>
                  <th>分值</th>
                  <th>大类名称</th>
                  <th>单项训练</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>什么是和弦？</td>
                  <td>单选题</td>
                  <td>三个音</td>
                  <td>两个音</td>
                  <td>四个音</td>
                  <td>五个音</td>
                  <td>A</td>
                  <td>10</td>
                  <td>练耳</td>
                  <td>比较音高低</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="format-example">
            <p class="example-title">录音题格式：</p>
            <table class="format-table">
              <thead>
                <tr>
                  <th>题目标题</th>
                  <th>题目类型</th>
                  <th>音频URL</th>
                  <th>音频时长(秒)</th>
                  <th>分值</th>
                  <th>大类名称</th>
                  <th>单项训练</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>请听音频并回答</td>
                  <td>录音题</td>
                  <td>/uploads/audio1.mp3</td>
                  <td>30</td>
                  <td>10</td>
                  <td>练耳</td>
                  <td>听辨音高</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="format-tips">
            <p class="tips-title">注意事项：</p>
            <ul>
              <li>题目类型：单选题、多选题、录音题</li>
              <li>正确答案：单选题填写选项字母（如A、B、C、D），多选题用逗号分隔（如A,B）</li>
              <li>分类：填写「大类名称」（如练耳、乐理）和「单项训练」（如比较音高低）；也可填小分类ID</li>
              <li>分值：建议设置为10分</li>
              <li>音频URL：录音题必须提供音频文件的URL路径</li>
            </ul>
          </div>
        </div>
      </div>

      <div class="upload-section">
        <h3>📤 上传题目文件</h3>
        <div class="upload-card">
          <van-uploader
            v-model="fileList"
            :after-read="afterRead"
            :before-delete="beforeDelete"
            accept=".xlsx,.xls,.csv"
            :max-size="10 * 1024 * 1024"
            @oversize="onOversize"
            upload-text="点击上传文件"
          />
          <div class="upload-tips">
            <p>支持上传 .xlsx、.xls、.csv 格式文件</p>
            <p>文件大小不超过 10MB</p>
          </div>
        </div>
      </div>

      <div class="action-section">
        <van-button
          type="primary"
          size="large"
          :loading="uploading"
          :disabled="fileList.length === 0"
          @click="handleUpload"
        >
          {{ uploading ? '上传中...' : '开始上传' }}
        </van-button>
      </div>

      <div class="result-section" v-if="uploadResult">
        <h3>📊 上传结果</h3>
        <div class="result-card" :class="{ 'success': uploadResult.success, 'error': !uploadResult.success }">
          <van-icon :name="uploadResult.success ? 'checked' : 'close'" size="48" />
          <h4>{{ uploadResult.success ? '上传成功' : '上传失败' }}</h4>
          <p v-if="uploadResult.message">{{ uploadResult.message }}</p>
          <div v-if="uploadResult.success && uploadResult.data" class="result-details">
            <p>成功导入：{{ uploadResult.data.success_count }} 道题目</p>
            <p v-if="uploadResult.data.failed_count > 0">失败：{{ uploadResult.data.failed_count }} 道题目</p>
            <p v-if="uploadResult.data.errors && uploadResult.data.errors.length > 0" class="error-list">
              错误详情：
              <span v-for="(error, index) in uploadResult.data.errors" :key="index">{{ error }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import * as XLSX from 'xlsx'

export default {
  name: 'AdminUpload',
  data() {
    return {
      fileList: [],
      uploading: false,
      uploadResult: null
    }
  },
  methods: {
    goBack() {
      this.$router.back()
    },
    afterRead(file) {
      console.log('文件已选择:', file)
    },
    beforeDelete(file) {
      this.fileList = []
      this.uploadResult = null
      return true
    },
    onOversize(file) {
      Toast('文件大小不能超过 10MB')
    },
    async handleUpload() {
      if (this.fileList.length === 0) {
        Toast('请先选择文件')
        return
      }

      this.uploading = true
      this.uploadResult = null

      try {
        const file = this.fileList[0].file
        const parsed = await this.parseFile(file)
        const questions = parsed.filter(q => !q.error)
        const parseErrors = parsed.filter(q => q.error).map(q => q.message)
        
        if (questions.length === 0) {
          throw new Error(parseErrors[0] || '文件中没有找到有效的题目数据')
        }

        const bankId = parseInt(localStorage.getItem('last_bank_id'), 10) || 0
        questions.forEach(q => {
          if (bankId > 0 && !q.bank_id) {
            q.bank_id = bankId
          }
        })

        const response = await this.$http.post('/api/admin/questions/upload', {
          questions: questions
        })

        if (response.data.code === 200) {
          this.uploadResult = {
            success: true,
            message: '题目上传成功',
            data: response.data.data
          }
          Toast.success('题目上传成功')
        } else {
          throw new Error(response.data.msg || '上传失败')
        }
      } catch (err) {
        console.error('上传失败:', err)
        this.uploadResult = {
          success: false,
          message: err.message || '上传失败，请检查文件格式'
        }
        Toast.fail(err.message || '上传失败')
      } finally {
        this.uploading = false
      }
    },
    parseFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        
        reader.onload = (e) => {
          try {
            const data = e.target.result
            const workbook = XLSX.read(data, { type: 'binary' })
            const firstSheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[firstSheetName]
            const jsonData = XLSX.utils.sheet_to_json(worksheet)
            
            const questions = this.convertToQuestions(jsonData)
            resolve(questions)
          } catch (err) {
            reject(new Error('文件解析失败：' + err.message))
          }
        }
        
        reader.onerror = () => {
          reject(new Error('文件读取失败'))
        }
        
        reader.readAsBinaryString(file)
      })
    },
    applyCategoryFields(row, question) {
      const categoryName = row['大类名称'] || row['分类名称'] || row['category_name'] || ''
      const subCategoryName = row['单项训练'] || row['单项训练名称'] || row['子分类名称'] || row['subcategory_name'] || ''
      const subId = parseInt(row['小分类ID'], 10)
      if (categoryName || subCategoryName) {
        question.category_name = String(categoryName).trim()
        question.subcategory_name = String(subCategoryName).trim()
      } else if (subId > 0) {
        question.sub_category_id = subId
      }
      const bankId = parseInt(localStorage.getItem('last_bank_id'), 10)
      if (bankId > 0) {
        question.bank_id = bankId
      }
    },
    buildOptionsJson(row, correctAnswer) {
      const optionKeys = ['A', 'B', 'C', 'D']
      const list = []
      optionKeys.forEach(key => {
        const text = row[`选项${key}`]
        if (text) {
          list.push({ label: key, value: String(text).trim() })
        }
      })
      return JSON.stringify(list)
    },
    convertToQuestions(jsonData) {
      const questions = []
      
      jsonData.forEach((row, index) => {
        try {
          const questionType = this.parseQuestionType(row['题目类型'])
          
          if (questionType === 3) {
            const question = {
              title: row['题目标题'],
              type: 3,
              audio_url: row['音频URL'] || '',
              audio_duration: parseInt(row['音频时长(秒)']) || 0,
              score: parseInt(row['分值']) || 10,
              status: 1
            }
            this.applyCategoryFields(row, question)
            
            if (!question.title) {
              throw new Error(`第${index + 2}行：题目标题不能为空`)
            }
            
            if (!question.audio_url) {
              throw new Error(`第${index + 2}行：录音题必须提供音频URL`)
            }
            if (!question.sub_category_id && !question.subcategory_name) {
              throw new Error(`第${index + 2}行：请填写大类名称+单项训练，或小分类ID`)
            }
            
            questions.push(question)
          } else {
            const question = {
              title: row['题目标题'],
              type: questionType,
              score: parseInt(row['分值']) || 10,
              status: 1
            }
            this.applyCategoryFields(row, question)
            
            if (!question.title) {
              throw new Error(`第${index + 2}行：题目标题不能为空`)
            }
            
            const correctAnswer = row['正确答案']
            if (!correctAnswer) {
              throw new Error(`第${index + 2}行：正确答案不能为空`)
            }
            
            question.options = this.buildOptionsJson(row, correctAnswer)
            if (question.options === '[]') {
              throw new Error(`第${index + 2}行：至少需要一个选项`)
            }
            question.correct_answer = String(correctAnswer).replace(/，/g, ',').toUpperCase()
            if (!question.sub_category_id && !question.subcategory_name) {
              throw new Error(`第${index + 2}行：请填写大类名称+单项训练，或小分类ID`)
            }
            questions.push(question)
          }
        } catch (err) {
          console.error(`第${index + 2}行解析失败:`, err)
          questions.push({
            error: true,
            message: `第${index + 2}行：${err.message}`
          })
        }
      })
      
      return questions
    },
    parseQuestionType(type) {
      if (!type) return 1
      
      const typeMap = {
        '单选题': 1,
        '多选题': 2,
        '录音题': 3
      }
      
      return typeMap[type] || 1
    }
  }
}
</script>

<style scoped>
.admin-upload-container {
  min-height: 100vh;
  background: #f8f9ff;
  padding-bottom: calc(60px + env(safe-area-inset-bottom));
}

.header {
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.content {
  padding: 20px;
}

.format-section,
.upload-section,
.result-section {
  margin-bottom: 24px;
}

.format-section h3,
.upload-section h3,
.result-section h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1a1a2e;
}

.format-card,
.upload-card,
.result-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.format-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 16px;
}

.format-example {
  margin-bottom: 20px;
}

.example-title {
  font-size: 14px;
  font-weight: 600;
  color: #764ba2;
  margin-bottom: 8px;
}

.format-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: #f8f9ff;
  border-radius: 8px;
  overflow: hidden;
}

.format-table th,
.format-table td {
  padding: 8px;
  text-align: left;
  border: 1px solid #e8f0ff;
}

.format-table th {
  background: #764ba2;
  color: white;
  font-weight: 600;
}

.format-table td {
  color: #1a1a2e;
}

.format-tips {
  background: #fff7e6;
  border-left: 4px solid #faad14;
  padding: 16px;
  border-radius: 8px;
}

.tips-title {
  font-size: 14px;
  font-weight: 600;
  color: #faad14;
  margin-bottom: 8px;
}

.format-tips ul {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
}

.format-tips li {
  font-size: 13px;
  color: #1a1a2e;
  margin-bottom: 6px;
  line-height: 1.6;
}

.upload-tips {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e8f0ff;
}

.upload-tips p {
  font-size: 13px;
  color: #999;
  margin: 4px 0;
}

.action-section {
  margin-top: 24px;
}

.result-card {
  text-align: center;
  padding: 32px 20px;
}

.result-card.success {
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
  color: white;
}

.result-card.error {
  background: linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%);
  color: white;
}

.result-card .van-icon {
  margin-bottom: 16px;
}

.result-card h4 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
}

.result-card p {
  font-size: 14px;
  margin: 4px 0;
  opacity: 0.95;
}

.result-details {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}

.error-list {
  margin-top: 12px;
  text-align: left;
  background: rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 8px;
}

.error-list span {
  display: block;
  font-size: 12px;
  margin-bottom: 4px;
  line-height: 1.5;
}
</style>
